import React, { useEffect, useMemo, useState } from "react";
import { StepProps } from "../../types/onboarding";
import styles from "./SelectIntegrations.module.css";
import vcStyles from "../vc-onboarding/VCOnboarding.module.css";
import InlineSpinner from "../../components/common/InlineSpinner";
import { useOnboarding } from "../../hooks/useOnboarding";
import { useAuth } from "../../contexts/AuthContext";
import { integrationsService } from "../../services/integrationsService";
import IntegrationLogo from "../../components/common/IntegrationLogo";
import IntegrationDataSelectionModal from "../../components/integrations/IntegrationDataSelectionModal";
import SlackChannelConfirmModal from "../../components/integrations/SlackChannelConfirmModal";
import { DataSelections } from "../../types/integrationOptions";
import {
  INTEGRATION_REGISTRY,
  CONNECTABLE_IDS,
  COMING_SOON_IDS,
  DISABLED_IDS,
  API_KEY_IDS,
  DATA_SELECTION_IDS,
  ICON_INVERT_IDS,
  BETA_IDS,
  ONBOARDING_CATEGORIES,
  COMPOSIO_IDS,
} from "../../config/integrations";
import { ComposioService } from "../../services/composioService";
import { useIntegrationModals } from "../../hooks/useIntegrationModals";

interface Integration {
  id: string;
  name: string;
  icon?: string;
  domain?: string;
  category?: string;
}

// Derive onboarding integrations from the shared registry
// Only include integrations that have an onboardingCategory assigned and are not hidden
const ONBOARDING_INTEGRATIONS: Integration[] = INTEGRATION_REGISTRY
  .filter((i) => i.onboardingCategory)
  .map((i) => ({
    id: i.id,
    name: i.name,
    icon: i.icon,
    domain: i.domain,
    category: i.onboardingCategory,
  }));

// Minimum integrations required to proceed
const MINIMUM_INTEGRATIONS_REQUIRED = 1;
// Maximum integrations allowed during onboarding
const MAXIMUM_INTEGRATIONS_ALLOWED = 3;

const SelectIntegrations: React.FC<StepProps> = ({ onNext, onBack }) => {
  const { user } = useAuth();
  const { setIntegrations } = useOnboarding();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [composioToolkits, setComposioToolkits] = useState<Integration[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [showDataSelectionModal, setShowDataSelectionModal] = useState(false);
  const [dataSelectionProvider, setDataSelectionProvider] = useState<
    string | null
  >(null);
  const [showSlackConfirmModal, setShowSlackConfirmModal] = useState(false);
  const [slackJoinedChannels, setSlackJoinedChannels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  // Queue data selection modal for after Slack confirm closes
  const [pendingDataSelectionProvider, setPendingDataSelectionProvider] =
    useState<string | null>(null);
  // Triggers auto-advance once the 3rd integration is connected and all modals are closed
  const [shouldAutoAdvance, setShouldAutoAdvance] = useState(false);

  // Shared pre-connection modal hook (GitHub org warning + API-key info modals)
  const doConnectRef = React.useRef<((id: string) => void) | null>(null);
  const handleNextRef = React.useRef<() => void>(() => undefined);
  const { preConnectCheck, renderModals } = useIntegrationModals(
    (id: string) => doConnectRef.current?.(id),
  );

  // Combine static + dynamic integrations
  const allOnboardingIntegrations = useMemo(() => {
    return [...ONBOARDING_INTEGRATIONS, ...composioToolkits];
  }, [composioToolkits]);

  // Flat filtered list for search + pagination
  const filteredIntegrations = useMemo(() => {
    if (!searchTerm) return allOnboardingIntegrations;
    const lower = searchTerm.toLowerCase();
    return allOnboardingIntegrations.filter((i) =>
      i.name.toLowerCase().includes(lower),
    );
  }, [allOnboardingIntegrations, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredIntegrations.length / itemsPerPage);
  const paginatedIntegrations = filteredIntegrations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset page on search change
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Group integrations by onboarding category (derived from shared registry + composio)
  const integrationsByCategory = useMemo(() => {
    const grouped: Record<string, Integration[]> = {};
    for (const cat of ONBOARDING_CATEGORIES) {
      grouped[cat.id] = allOnboardingIntegrations.filter(
        (i) => i.category === cat.id,
      );
    }
    // Add an "Other" category for Composio apps without a matching onboarding category
    const knownCats = new Set<string>(ONBOARDING_CATEGORIES.map((c) => c.id));
    const other = allOnboardingIntegrations.filter((i) => !knownCats.has(i.category || ""));
    if (other.length > 0) grouped["other"] = other;
    return grouped;
  }, [allOnboardingIntegrations]);

  // Filter integrations based on search term (for category view)
  const filteredIntegrationsByCategory = useMemo(() => {
    if (!searchTerm) return integrationsByCategory;

    const filtered: Record<string, Integration[]> = {};
    Object.entries(integrationsByCategory).forEach(
      ([category, integrations]) => {
        const matches = integrations.filter((i) =>
          i.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        if (matches.length > 0) {
          filtered[category] = matches;
        }
      },
    );
    return filtered;
  }, [integrationsByCategory, searchTerm]);

  // Load connected integrations so the user sees status and we gate Next
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!user) return;
        const list: any[] = await integrationsService.getUserIntegrations(true);
        if (!mounted) return;
        const active = (list || [])
          .filter((i: any) => i?.integration_status === "active")
          .map((i: any) => i.provider.toLowerCase());
        setConnectedProviders(active);
        setIntegrations(active);
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user, setIntegrations]);

  // Load all Composio toolkits dynamically (950+ apps)
  useEffect(() => {
    (async () => {
      try {
        const toolkits = await ComposioService.listToolkits();
        const normalize = (s: string) => s.toLowerCase().replace(/[-_\s]+/g, "");
        const staticIds = new Set(ONBOARDING_INTEGRATIONS.map((i) => normalize(i.id)));
        const staticNames = new Set(ONBOARDING_INTEGRATIONS.map((i) => normalize(i.name)));
        const seen = new Set<string>();
        const dynamic: Integration[] = toolkits
          .map((t: any) => ({ ...t, _id: (t.key || t.appId || t.name || "").toLowerCase() }))
          .filter((t: any) => {
            if (!t._id) return false;
            const norm = normalize(t._id);
            if (staticIds.has(norm) || staticNames.has(normalize(t.name || "")) || seen.has(norm)) return false;
            seen.add(norm);
            return true;
          })
          .map((t: any) => ({
            id: t._id,
            name: t.name || t.key || "Unknown",
            icon: t.logo || undefined,
            domain: undefined,
            category: (t.categories?.[0]?.toLowerCase() || "productivity") as string,
          }));
        setComposioToolkits(dynamic);
      } catch { /* ignore */ }
    })();
  }, []);

  // Helper to refresh connected providers list
  const refreshConnectedProviders = async () => {
    const list = await integrationsService.getUserIntegrations(true);
    const active = (list || [])
      .filter((i: any) => i?.integration_status === "active")
      .map((i: any) => i.provider.toLowerCase());
    setConnectedProviders(active);
    setIntegrations(active);
    if (active.length >= MAXIMUM_INTEGRATIONS_ALLOWED) {
      setShouldAutoAdvance(true);
    }
  };

  // Actual connection logic (called directly or after modal confirmation via hook)
  const doConnect = async (id: string) => {
    setError(null);
    if (!CONNECTABLE_IDS.has(id) && !composioToolkitIds.has(id)) return;

    // Route Composio integrations through ComposioService
    if (COMPOSIO_IDS.has(id) || composioToolkitIds.has(id)) {
      try {
        setConnectingId(id);
        const result = await ComposioService.initiateConnection(id);
        const redirectUrl = result.redirectUrl;
        if (redirectUrl) {
          const width = 500;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          window.open(
            redirectUrl,
            `composio_connect_${id}`,
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
          );
          // Poll for connection. Keep the last failure around so a timeout can
          // report *why* it failed rather than a bare "timed out" — a silent
          // timeout is what hid the Composio API errors in the first place.
          let attempts = 0;
          let lastError: string | null = null;
          let settled = false;

          const finish = async () => {
            if (settled) return;
            settled = true;
            clearInterval(pollInterval);
            window.removeEventListener("message", onPopupMessage);
            setConnectingId(null);
            setError(null);
            await refreshConnectedProviders();
          };

          // The callback page posts here the moment Composio hands the popup
          // back, so the UI advances immediately instead of waiting out the
          // next 2s tick. Polling stays as the fallback for blocked popups and
          // users who finish auth in a separate tab.
          const onPopupMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.source !== "composio") return;
            if (event.data?.type !== "OAUTH_SUCCESS") return;
            void finish();
          };
          window.addEventListener("message", onPopupMessage);

          const pollInterval = setInterval(async () => {
            attempts++;
            try {
              const status = await ComposioService.getConnectionStatus(id);
              if (status.connected) {
                await finish();
                return;
              }
              // Backend reports a reachable-but-failing Composio check here.
              lastError = status.error || null;
            } catch (err) {
              lastError = err instanceof Error ? err.message : "Status check failed";
            }
            if (attempts >= 30 && !settled) {
              settled = true;
              clearInterval(pollInterval);
              window.removeEventListener("message", onPopupMessage);
              setConnectingId(null);
              setError(
                lastError
                  ? `Couldn't confirm the connection: ${lastError}`
                  : "Connection is still being processed. Refresh in a moment to check."
              );
              // The connection may still land after we stop polling, so re-read
              // the server's view rather than leaving the UI stuck.
              try {
                await refreshConnectedProviders();
              } catch {
                // Already surfacing a message above; a failed refresh adds nothing.
              }
            }
          }, 2000);
        }
      } catch (err) {
        setConnectingId(null);
        setError(err instanceof Error ? err.message : "Failed to connect");
      }
      return;
    }

    try {
      setConnectingId(id);
      await integrationsService.connectIntegration(id, {
        forceReconnect: true, // Always force re-authentication from onboarding
        onSuccess: async () => {
          console.log(`Successfully connected ${id}`);

          // The integrationsService.connectIntegration() already called
          // syncProviderConnection inside the Nango connect event handler,
          // so the DB record should exist. We just need to verify and
          // update UI state. If the sync was too fast (Nango still
          // finalizing), fall back to polling with increasing delays and
          // use sync=true on the last attempt as a safety net.
          const checkConnection = async (): Promise<boolean> => {
            const delays = [0, 800, 1500, 2000, 3000, 5000, 8000, 12000];
            const totalAttempts = delays.length;

            for (let attempt = 0; attempt < totalAttempts; attempt++) {
              if (delays[attempt] > 0) {
                await new Promise((r) => setTimeout(r, delays[attempt]));
              }
              try {
                // Use sync=true on the last attempt to trigger full
                // Nango reconciliation as a safety net
                const useSync = attempt === totalAttempts - 1;
                const list =
                  await integrationsService.getUserIntegrations(useSync);
                const active = (list || [])
                  .filter(
                    (i) => i?.integration_status === "active",
                  )
                  .map((i) => i.provider.toLowerCase());
                if (
                  active.some(
                    (p) => p.toLowerCase() === id.toLowerCase(),
                  )
                ) {
                  setConnectedProviders(active);
                  setIntegrations(active);
                  if (active.length >= MAXIMUM_INTEGRATIONS_ALLOWED) {
                    setShouldAutoAdvance(true);
                  }
                  console.log(
                    `Connection verified for ${id} on attempt ${attempt + 1}`,
                  );
                  return true;
                }
              } catch (error) {
                console.error(
                  `Connection check attempt ${attempt + 1} failed:`,
                  error,
                );
              }
            }
            return false;
          };

          const connected = await checkConnection();
          setConnectingId(null);

          if (!connected) {
            setError(
              `Failed to verify ${id} connection. Please try reconnecting or contact support.`,
            );
          } else {
            // Special handling for Slack - auto-join public channels
            if (id.toLowerCase() === "slack") {
              try {
                const autoJoinResult =
                  await integrationsService.autoJoinSlackChannels();
                if (
                  autoJoinResult.success &&
                  autoJoinResult.joined.length > 0
                ) {
                  setSlackJoinedChannels(autoJoinResult.joined);
                  setShowSlackConfirmModal(true);
                } else {
                  setSlackJoinedChannels([]);
                  setShowSlackConfirmModal(true);
                }
              } catch (autoJoinError) {
                console.error(
                  `Slack auto-join failed, but connection successful:`,
                  autoJoinError,
                );
                setSlackJoinedChannels([]);
                setShowSlackConfirmModal(true);
              }
              // Queue data selection to show after Slack confirm closes
              if (DATA_SELECTION_IDS.has(id.toLowerCase())) {
                setPendingDataSelectionProvider(id.toLowerCase());
              }
            } else if (DATA_SELECTION_IDS.has(id.toLowerCase())) {
              // Trigger data selection modal for non-Slack providers
              setDataSelectionProvider(id.toLowerCase());
              setShowDataSelectionModal(true);
            }

            // Fire-and-forget: warm the integration data cache so
            // insight generation in PrepareUpdate hits warm cache
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            integrationsService.getIntegrationData(id.toLowerCase()).catch(() => {});
          }
        },
        onError: (error: Error) => {
          console.error(`Failed to connect ${id}:`, error);
          setError(
            error?.message || `Failed to connect ${id}. Please try again.`,
          );
          setConnectingId(null);
        },
        onClose: () => {
          console.log(`User closed ${id} connection dialog`);
          setConnectingId(null);
        },
      });
    } catch (e: any) {
      setError(e?.message || `Failed to connect ${id}. Please try again.`);
      setConnectingId(null);
    }
  };
  doConnectRef.current = doConnect;

  const handleTileClick = (id: string) => {
    setError(null);
    if ((!CONNECTABLE_IDS.has(id) && !composioToolkitIds.has(id)) || DISABLED_IDS.has(id)) return;
    const isAlreadyConnected = connectedProviders.some(
      (p) => p.toLowerCase() === id.toLowerCase(),
    );
    if (!isAlreadyConnected && connectedProviders.length >= MAXIMUM_INTEGRATIONS_ALLOWED) return;
    if (!preConnectCheck(id)) return;
    doConnect(id);
  };


  const handleDataSelectionSave = async (selections: DataSelections) => {
    if (!dataSelectionProvider) return;

    try {
      await integrationsService.updateIntegrationSettings(
        dataSelectionProvider,
        {
          selectedRepositories: selections.selectedIds,
          selectedKinds: selections.selectedKinds,
          idToNameMap: selections.idToNameMap || {},
          lastConfigured: new Date().toISOString(),
        },
      );

      console.log(`✅ Saved data selections for ${dataSelectionProvider}`);
      setShowDataSelectionModal(false);
      setDataSelectionProvider(null);
    } catch (err: any) {
      console.error(
        `❌ Failed to save selections for ${dataSelectionProvider}:`,
        err,
      );
      setError(
        err?.message ||
          `Failed to save ${dataSelectionProvider} selections. Please try again.`,
      );
      setShowDataSelectionModal(false);
      setDataSelectionProvider(null);
    }
  };

  const handleNext = async () => {
    if (!user) {
      setError("User not found. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (connectedProviders.length < MINIMUM_INTEGRATIONS_REQUIRED) {
        throw new Error(
          `Please connect at least ${MINIMUM_INTEGRATIONS_REQUIRED} integration to continue.`,
        );
      }

      // Clear PrepareUpdate insight cache so it regenerates with the
      // current set of integrations instead of serving stale results.
      try {
        localStorage.removeItem("onboarding_prepare_generated");
        localStorage.removeItem("onboarding_prepare_selected_idx");
      } catch (_e) {
        // localStorage may be unavailable
      }

      // Let OnboardingContainer handle persistence via onNext → save()
      onNext && onNext({ integrations: connectedProviders });
    } catch (error) {
      console.error("Failed to save integrations:", error);
      setError(
        (error as any)?.message ||
          "Failed to save your integrations. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  handleNextRef.current = handleNext;

  // Auto-advance once the max integrations are connected and all modals have closed
  useEffect(() => {
    if (
      shouldAutoAdvance &&
      !showSlackConfirmModal &&
      !showDataSelectionModal &&
      !connectingId
    ) {
      setShouldAutoAdvance(false);
      handleNextRef.current();
    }
  }, [shouldAutoAdvance, showSlackConfirmModal, showDataSelectionModal, connectingId]);

  // Dynamic Composio toolkit IDs (not in static registry) are always connectable
  const composioToolkitIds = new Set(composioToolkits.map((t) => t.id));

  const renderIntegrationCard = (integration: Integration) => {
    const isDynamicComposio = composioToolkitIds.has(integration.id);
    const isConnectable = CONNECTABLE_IDS.has(integration.id) || isDynamicComposio;
    const isDisabled = DISABLED_IDS.has(integration.id);
    const isComingSoon = !isDynamicComposio && COMING_SOON_IDS.has(integration.id);
    const isConnected = connectedProviders.some(
      (p) => p.toLowerCase() === integration.id.toLowerCase(),
    );
    const isConnecting = connectingId === integration.id;
    const requiresApiKey = API_KEY_IDS.has(integration.id);
    const isBeta = BETA_IDS.has(integration.id);
    const isAtMax = !isConnected && connectedProviders.length >= MAXIMUM_INTEGRATIONS_ALLOWED;
    const isClickable = isConnectable && !isDisabled && !isComingSoon && !isAtMax;

    return (
      <button
        key={integration.id}
        type="button"
        className={`${vcStyles.integrationCard} ${isConnected ? vcStyles.selected : ""} ${!isClickable ? styles.tileDisabled : ""}`}
        onClick={() => isClickable && handleTileClick(integration.id)}
        disabled={!isClickable || isConnecting}
        data-testid={`integration-${integration.id}`}
        style={{
          border: isConnected ? "2px solid #10b981" : undefined,
          position: "relative",
          opacity: isClickable ? 1 : 0.5,
          cursor: isClickable ? "pointer" : "not-allowed",
        }}
      >
        {/* Show "Coming Soon" badge */}
        {isComingSoon && (
          <span
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              background: "#6b7280",
              color: "white",
              padding: "2px 6px",
              fontSize: "10px",
              fontWeight: 600,
              borderRadius: "3px",
              zIndex: 1,
            }}
          >
            Coming Soon
          </span>
        )}

        {/* Show "API Key" badge for integrations that require API keys */}
        {requiresApiKey && (
          <span
            className="su-badge-apikey"
            style={{
              position: "absolute",
              top: "4px",
              left: "4px",
              zIndex: 1,
            }}
          >
            API Key
          </span>
        )}

        {/* Show "Beta" badge */}
        {isBeta && !isComingSoon && (
          <span
            className="su-badge-beta"
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              zIndex: 1,
            }}
          >
            Beta
          </span>
        )}

        {/* Show "Connected" badge if connected */}
        {isConnected && (
          <span
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              background: "#10b981",
              color: "white",
              padding: "2px 6px",
              fontSize: "10px",
              fontWeight: 600,
              borderRadius: "3px",
              zIndex: 1,
            }}
          >
            Connected
          </span>
        )}

        {isConnecting && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <InlineSpinner />
          </div>
        )}
        <div className={vcStyles.integrationContent}>
          <div className={vcStyles.integrationIcon}>
            <IntegrationLogo
              domain={integration.domain}
              name={integration.name}
              fallbackImage={integration.icon}
              className={`w-full h-full ${
                ICON_INVERT_IDS.has(integration.id)
                  ? vcStyles.iconInvert
                  : ""
              }`}
            />
          </div>
          <div className={vcStyles.integrationName}>
            {isConnecting ? "Connecting..." : integration.name}
          </div>
        </div>
        <div className={vcStyles.integrationCheckbox}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </button>
    );
  };

  return (
    <div className={styles.root}>
      <h1 className={styles.header}>Connect Your Tools</h1>
      <p className={styles.subheader}>
        Select up to {MAXIMUM_INTEGRATIONS_ALLOWED} integrations that will give
        the system the most information about your work.
      </p>

      <div
        className={vcStyles.integrationCard}
        style={{
          marginBottom: "1.5rem",
          background: "#1e1b4b",
          borderColor: "#5b21b6",
        }}
      >
        <p style={{ fontSize: "0.875rem", color: "#c4b5fd", margin: 0 }}>
          {connectedProviders.length} / {MAXIMUM_INTEGRATIONS_ALLOWED} integrations connected
          {connectedProviders.length >= MINIMUM_INTEGRATIONS_REQUIRED && " ✓"}
          {connectedProviders.length >= MAXIMUM_INTEGRATIONS_ALLOWED && " — maximum reached"}
        </p>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: "#1f0f0f",
            border: "1px solid #ef4444",
            color: "#ef4444",
            padding: "10px 12px",
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {/* Pre-connection modals (GitHub org warning + API key info modals) */}
      {renderModals()}

      {/* Integration Data Selection Modal */}
      <IntegrationDataSelectionModal
        isOpen={showDataSelectionModal}
        onClose={() => {
          setShowDataSelectionModal(false);
          setDataSelectionProvider(null);
        }}
        onSave={handleDataSelectionSave}
        provider={dataSelectionProvider || ""}
        providerName={
          dataSelectionProvider
            ? ONBOARDING_INTEGRATIONS.find(
                (i) =>
                  i.id.toLowerCase() === dataSelectionProvider.toLowerCase(),
              )?.name || dataSelectionProvider
            : ""
        }
      />

      {/* Slack Channel Confirmation Modal */}
      <SlackChannelConfirmModal
        isOpen={showSlackConfirmModal}
        onClose={() => {
          setShowSlackConfirmModal(false);
          // Show queued data selection modal after Slack confirm closes
          if (pendingDataSelectionProvider) {
            setDataSelectionProvider(pendingDataSelectionProvider);
            setShowDataSelectionModal(true);
            setPendingDataSelectionProvider(null);
          }
        }}
        joinedChannels={slackJoinedChannels}
      />

      {/* Search bar */}
      <div style={{ marginBottom: "1rem" }}>
        <input
          type="text"
          placeholder={`Search ${allOnboardingIntegrations.length}+ integrations...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.search}
          style={{ width: "100%", maxWidth: "500px", padding: "10px 16px", fontSize: "14px" }}
          data-testid="integration-search"
        />
      </div>

      {/* Pagination info */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", fontSize: "13px", color: "#999" }}>
        <span>Showing {totalPages === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredIntegrations.length)} of {filteredIntegrations.length} integrations</span>
      </div>

      {/* Integrations — flat paginated grid */}
      <div className={styles.integrationsContainer}>
        <div className={vcStyles.integrationGrid}>
          {paginatedIntegrations.map(renderIntegrationCard)}
        </div>

        {searchTerm && filteredIntegrations.length === 0 && (
          <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>
            No integrations found matching &ldquo;{searchTerm}&rdquo;
          </div>
        )}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "16px" }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", color: currentPage === 1 ? "#555" : "#fff", border: "1px solid #333", borderRadius: "4px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) page = i + 1;
              else if (currentPage <= 3) page = i + 1;
              else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
              else page = currentPage - 2 + i;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: "6px 12px", fontSize: "13px",
                    background: currentPage === page ? "#7C3AED" : "transparent",
                    color: currentPage === page ? "#fff" : "#999",
                    border: "1px solid #333", borderRadius: "4px", cursor: "pointer",
                    fontWeight: currentPage === page ? 700 : 400,
                  }}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", color: currentPage === totalPages ? "#555" : "#fff", border: "1px solid #333", borderRadius: "4px", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {connectedProviders.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              marginBottom: 8,
              color: "#34d399",
              fontWeight: 600,
            }}
          >
            Connected ({connectedProviders.length})
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {connectedProviders.map((pid) => {
              const integration = ONBOARDING_INTEGRATIONS.find(
                (i) => i.id.toLowerCase() === pid.toLowerCase(),
              );
              const displayName = integration?.name || pid;
              return (
                <div
                  key={pid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid #34d399",
                    color: "#34d399",
                    padding: "8px 10px",
                  }}
                >
                  <span style={{ textTransform: "capitalize" }}>
                    {displayName}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      Connected
                    </span>
                    <span aria-hidden>⚡</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className={styles.footer}>
        {onBack && (
          <button className={styles.secondaryButton} onClick={onBack}>
            Back
          </button>
        )}
        <button
          className={styles.primaryButton}
          onClick={handleNext}
          disabled={
            isSubmitting ||
            connectedProviders.length < MINIMUM_INTEGRATIONS_REQUIRED
          }
          data-testid="next-button"
        >
          {isSubmitting ? <InlineSpinner /> : "Next"}
        </button>
      </div>
    </div>
  );
};

export default SelectIntegrations;

// Export for testing
export {
  ONBOARDING_INTEGRATIONS,
  MINIMUM_INTEGRATIONS_REQUIRED,
};
